import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Table, Tag, Input, Select, Space, Typography, Button, message, Modal, Form, Popconfirm, Tabs, Avatar, Collapse, Spin, Empty } from 'antd';
import { SearchOutlined, SwapOutlined, DeleteOutlined } from '@ant-design/icons';
import { TournamentService } from '@/services/tournament.service';
import { Tournament } from '@/types/api.types';
import { logger } from '@/utils/logger';
import ParticipantStatistics from '@/components/profile/ParticipantStatistics';
import { useAuth } from '@/contexts/AuthContext';
import {
  FiltersContainer,
  SidebarContainer,
  ClubCard,
  MainContentContainer
} from './styles/TournamentParticipants.styles';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';

const { Text } = Typography;
const { Option } = Select;

interface TournamentParticipantsProps {
  tournamentId: number;
  tournament: Tournament;
  onCategoryClick?: (categoryId: number) => void;
}

interface ClubWithCount {
  id: number;
  name: string;
  participantCount: number;
}

// Функция для расчета возраста
const calculateAge = (dateOfBirth: string | Date | null | undefined): number | null => {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Функция для получения инициалов
const getInitials = (firstName: string, lastName: string, middleName?: string): string => {
  const first = firstName?.[0]?.toUpperCase() || '';
  const last = lastName?.[0]?.toUpperCase() || '';
  const middle = middleName?.[0]?.toUpperCase() || '';
  return `${last}${first}${middle}`.slice(0, 2);
};

export const TournamentParticipants: React.FC<TournamentParticipantsProps> = ({
  tournamentId,
  tournament,
  onCategoryClick,
}) => {
  const { user } = useAuth();
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [categorySearchText, setCategorySearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [selectedClubId, setSelectedClubId] = useState<number | undefined>(undefined);
  const [categories, setCategories] = useState<any[]>([]);
  const [clubs, setClubs] = useState<ClubWithCount[]>([]);
  const [activeTab, setActiveTab] = useState<string>('participants');
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null);
  const [changeCategoryModalVisible, setChangeCategoryModalVisible] = useState(false);
  const [selectedParticipantForCategoryChange, setSelectedParticipantForCategoryChange] = useState<any | null>(null);
  const [unregisteringClubId, setUnregisteringClubId] = useState<number | null>(null);
  const [form] = Form.useForm();
  
  // Пагинация
  const [_page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 50;
  const observerTarget = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  
  // Проверяем права на изменение категории (только для своих спортсменов)
  const canChangeCategory = (participantClubId?: number) => {
    if (!user) return false;
    
    // Если нет clubId у участника, нельзя изменить категорию
    if (!participantClubId) return false;
    
    // Менеджер клуба или менеджер может изменять категорию только для своих спортсменов
    if ((user.role === 'manager') && user.clubId && participantClubId === user.clubId) {
      return true;
    }
    
    // Организатор турнира может изменять категорию только для своих спортсменов
    // (если организатор - менеджер клуба, то только для спортсменов своего клуба)
    if (tournament?.organizerId === user.id) {
      // Если у организатора есть клуб, то только для спортсменов своего клуба
      if (user.clubId) {
        return participantClubId === user.clubId;
      }
      // Если у организатора нет клуба, но он организатор - не разрешаем (только для своих спортсменов)
      return false;
    }
    
    return false;
  };

  // Организатор/админ/менеджер клуба-организатора может снять любую команду с турнира
  const canUnregisterClubAsOrganizer = () => {
    if (!user || !tournament) return false;
    const isAdmin = user.role === 'admin';
    const isOrganizer = tournament.organizerId === user.id;
    const isManagerOfOrganizerClub = (user.role === 'manager' || user.role === 'head_manager') &&
      user.clubId && tournament.clubId && tournament.clubId === user.clubId;
    return isAdmin || isOrganizer || isManagerOfOrganizerClub;
  };

  // Проверяем права на снятие с участия (только для своих спортсменов)
  const canUnregister = (participantClubId?: number) => {
    if (!user) return false;
    
    // Если нет clubId у участника, нельзя снять с участия
    if (!participantClubId) return false;
    
    // Менеджер клуба или менеджер может снимать с участия только своих спортсменов
    if ((user.role === 'manager') && user.clubId && participantClubId === user.clubId) {
      return true;
    }
    
    // Организатор турнира может снимать с участия только своих спортсменов
    // (если организатор - менеджер клуба, то только для спортсменов своего клуба)
    if (tournament?.organizerId === user.id) {
      // Если у организатора есть клуб, то только для спортсменов своего клуба
      if (user.clubId) {
        return participantClubId === user.clubId;
      }
      // Если у организатора нет клуба, но он организатор - не разрешаем (только для своих спортсменов)
      return false;
    }
    
    return false;
  };

  const handleParticipantClick = (participantId: number) => {
    setSelectedParticipantId(participantId);
    setStatisticsModalVisible(true);
  };

  const fetchParticipants = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    if (!tournamentId) {
      logger.warn('TournamentParticipants: tournamentId is missing');
      return;
    }
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      // API фильтрует по tp.categoryId (categories.id); в состоянии selectedCategory — tournament_categories.id
      const categoryIdForApi = selectedCategory && categories.length
        ? (categories.find(c => c.id === selectedCategory)?.categoryId ?? selectedCategory)
        : undefined;
      const result = await TournamentService.getRegisteredParticipants(
        tournamentId, 
        categoryIdForApi, 
        pageNum, 
        limit,
        selectedClubId
      );
      
      const participantsData = result.data || [];
      const pagination = result.pagination;
      
      // Обновляем информацию о пагинации
      if (pagination) {
        setHasMore(pagination.hasMore !== false && participantsData.length > 0);
        setTotal(pagination.total || 0);
      } else {
        // Если pagination отсутствует, определяем hasMore по количеству данных
        const hasMoreData = participantsData.length >= limit && participantsData.length > 0;
        setHasMore(hasMoreData);
        // Если total не пришел, используем текущее количество участников
        if (append) {
          setTotal(prev => Math.max(prev, participants.length + participantsData.length));
        } else {
          setTotal(participantsData.length);
        }
      }
      
      // Если данных нет, значит больше загружать нечего
      if (participantsData.length === 0) {
        setHasMore(false);
      }
      
      // Преобразуем данные участников в единый формат
      const formattedParticipants = participantsData.map((p: any) => {
        const participant = p.participant || p.user || p;
        const club = participant?.club || p.club;
        
        // Извлекаем clubId из всех возможных мест (включая прямое поле из бэкенда)
        // ПРИОРИТЕТ: p.clubId - это поле, которое бэкенд возвращает напрямую
        const clubId = p.clubId !== null && p.clubId !== undefined ? p.clubId :  // Прямое поле из бэкенда
                      club?.id || 
                      participant?.clubId || 
                      participant?.club_id ||
                      p.club_id ||
                      p.user?.clubId ||
                      p.user?.club?.id ||
                      p.participant?.clubId ||
                      p.participant?.club?.id;
        
        const dateOfBirth = participant?.dateOfBirth || participant?.date_of_birth || p.dateOfBirth;
        const age = calculateAge(dateOfBirth);
        const gender = participant?.gender || p.gender;
        
        return {
          id: p.id || participant?.id,
          participantId: p.participantId || p.userId || participant?.id,
          userId: p.userId || participant?.id,
          categoryId: p.categoryId || p.category_id,
          tournamentId: p.tournamentId || p.tournament_id,
          firstName: participant?.firstName || participant?.first_name || '',
          lastName: participant?.lastName || participant?.last_name || '',
          middleName: participant?.middleName || participant?.middle_name,
          weight: participant?.weight || p.weight,
          beltLevel: participant?.beltLevel || participant?.belt_level || p.beltLevel,
          dateOfBirth: dateOfBirth,
          age: age,
          gender: gender,
          club: club,
          clubId: clubId !== null && clubId !== undefined ? Number(clubId) : undefined,
          clubName: participant?.clubName || club?.name || p.clubName,
          city: participant?.city || p.city,
          status: p.status || 'registered',
          raw: p, // Сохраняем полный объект из бэкенда
        };
      });

      if (append) {
        // При добавлении новых данных проверяем на дубликаты
        setParticipants(prev => {
          // Создаем Set для отслеживания уже существующих ID
          const existingIds = new Set(prev.map(p => {
            // Используем комбинацию id, userId и participantId для уникальности
            return `${p.id || ''}-${p.userId || ''}-${p.participantId || ''}-${p.categoryId || ''}`;
          }));
          
          // Фильтруем новые участники, оставляя только тех, кого еще нет
          const newParticipants = formattedParticipants.filter(p => {
            const uniqueKey = `${p.id || ''}-${p.userId || ''}-${p.participantId || ''}-${p.categoryId || ''}`;
            return !existingIds.has(uniqueKey);
          });
          
          // Если новых участников нет или их меньше лимита, значит больше данных нет
          if (newParticipants.length === 0 || newParticipants.length < limit) {
            setHasMore(false);
          }
          
          return [...prev, ...newParticipants];
        });
      } else {
        setParticipants(formattedParticipants);
        // Если загружено меньше лимита, значит больше данных нет
        if (formattedParticipants.length < limit) {
          setHasMore(false);
        }
      }
    } catch (error) {
      logger.error('Error fetching participants:', error);
      // При ошибке также сбрасываем hasMore, чтобы не пытаться загружать дальше
      if (append) {
        setHasMore(false);
      }
      if (!append) {
        message.error('Ошибка при загрузке участников');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [tournamentId, selectedCategory, selectedClubId, limit, categories]);

  useEffect(() => {
    if (!tournamentId) {
      return;
    }
    
    // Сбрасываем пагинацию при изменении турнира или фильтров
    setPage(1);
    setHasMore(true);
    setParticipants([]);
    fetchParticipants(1, false);
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, selectedCategory, selectedClubId]);

  // Загрузка следующей страницы
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setPage(prevPage => {
        const nextPage = prevPage + 1;
        fetchParticipants(nextPage, true);
        return nextPage;
      });
    }
  }, [loadingMore, hasMore, loading, fetchParticipants]);

  // Intersection Observer для бесконечной прокрутки
  useEffect(() => {
    // Не создаем observer, если нет данных для загрузки или уже идет загрузка
    if (!hasMore || loadingMore || loading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Дополнительная проверка перед вызовом loadMore
        if (
          entries[0].isIntersecting && 
          hasMore && 
          !loadingMore && 
          !loading && 
          !isLoadingRef.current
        ) {
          isLoadingRef.current = true;
          loadMore();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px' // Начинаем загрузку за 100px до достижения элемента
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget && hasMore) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, loadMore]);

  const fetchCategories = async () => {
    if (!tournamentId) {
      logger.warn('TournamentParticipants: tournamentId is missing for fetchCategories');
      return;
    }
    
    try {
      const categoriesData = await TournamentService.getTournamentCategories(tournamentId);
      setCategories(categoriesData || []);
    } catch (error) {
      logger.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  // Загружаем клубы с бэкенда (все клубы и точное количество участников), а не из текущей страницы участников
  const fetchClubs = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const clubsData = await TournamentService.getRegisteredClubs(tournamentId);
      setClubs(clubsData);
    } catch (error) {
      logger.error('Error fetching clubs:', error);
      setClubs([]);
    }
  }, [tournamentId]);

  useEffect(() => {
    if (!tournamentId) return;
    fetchClubs();
  }, [tournamentId, fetchClubs]);

  const handleChangeCategory = (record: any) => {
    setSelectedParticipantForCategoryChange(record);
    // Форма ожидает tournament_categories.id; record.categoryId может быть categories.id или tournament_categories.id
    const cat = categories.find((c: any) => c.id === record.categoryId || (c.categoryId ?? c.id) === record.categoryId);
    form.setFieldsValue({ categoryId: cat?.id ?? record.categoryId });
    setChangeCategoryModalVisible(true);
  };

  const handleCategoryChangeSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { categoryId } = values;
      
      if (!selectedParticipantForCategoryChange) return;

      // Используем userId для поиска регистрации в бэкенде (бэкенд ищет по userId)
      const participantId = selectedParticipantForCategoryChange.userId || selectedParticipantForCategoryChange.participantId;
      
      if (!participantId) {
        message.error('Не удалось определить ID участника');
        return;
      }
      
      const currentCatId = selectedParticipantForCategoryChange.categoryId;
      const selectedIsCurrent = eligibleCategoriesForParticipant.some(
        (c: any) => c.id === categoryId && (c.id === currentCatId || (c.categoryId ?? c.id) === currentCatId)
      );
      if (selectedIsCurrent) {
        message.info('Категория не изменилась');
        setChangeCategoryModalVisible(false);
        return;
      }

      const result = await TournamentService.updateParticipantCategory(tournamentId, participantId, categoryId);
      if (result.categoriesNeedingReform > 0) {
        message.success(
          `Категория участника успешно обновлена. В ${result.categoriesNeedingReform} категории(ях) нужно переформировать сетки на странице жеребьёвки.`
        );
      } else {
        message.success('Категория участника успешно обновлена');
      }
      setChangeCategoryModalVisible(false);
      setSelectedParticipantForCategoryChange(null);
      form.resetFields();
      fetchParticipants(); // Обновляем список участников
    } catch (error: any) {
      logger.error('Error changing participant category:', error);
      message.error(error?.response?.data?.message || 'Ошибка при изменении категории участника');
    }
  };

  const handleUnregisterParticipant = async (record: any) => {
    try {
      const participantId = record.userId || record.participantId || record.id;
      await TournamentService.unregisterParticipant(tournamentId, participantId);
      message.success('Участник успешно снят с регистрации');
      fetchParticipants();
      fetchClubs();
    } catch (error: any) {
      logger.error('Error unregistering participant:', error);
      message.error(error?.response?.data?.message || 'Ошибка при снятии участника с регистрации');
    }
  };

  const handleUnregisterClub = async (club: ClubWithCount) => {
    try {
      setUnregisteringClubId(club.id);
      const result = await TournamentService.unregisterClub(tournamentId, club.id);
      message.success(
        result.bracketsReformed > 0
          ? `Команда «${club.name}» снята с турнира. Удалено ${result.removedParticipants} участников. Пересформирована сетка в ${result.bracketsReformed} категории(ях).`
          : `Команда «${club.name}» снята с турнира. Удалено ${result.removedParticipants} участников.`
      );
      fetchParticipants(1, false);
      fetchClubs();
      setSelectedClubId(undefined);
    } catch (error: any) {
      logger.error('Error unregistering club:', error);
      message.error(error?.response?.data?.message || 'Ошибка при снятии команды с турнира');
    } finally {
      setUnregisteringClubId(null);
    }
  };

  // Фильтрация участников
  const filteredParticipants = useMemo(() => {
    const selectedCat = selectedCategory ? categories.find(c => c.id === selectedCategory) : null;
    const selectedCategoryIdForFilter = selectedCat ? (selectedCat.categoryId ?? selectedCat.id) : null;
    return participants.filter((p) => {
      const matchesSearch = !searchText || 
        `${p.firstName} ${p.lastName} ${p.middleName || ''}`.toLowerCase().includes(searchText.toLowerCase()) ||
        p.clubName?.toLowerCase().includes(searchText.toLowerCase()) ||
        p.city?.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesCategory = !selectedCategoryIdForFilter || p.categoryId === selectedCategoryIdForFilter;
      const matchesClub = !selectedClubId || p.clubId === selectedClubId;
      
      return matchesSearch && matchesCategory && matchesClub;
    });
  }, [participants, searchText, selectedCategory, selectedClubId, categories]);

  // Фильтрация категорий для вкладки категорий (сортировка: возраст → вес → девочки перед мальчиками)
  const filteredCategories = useMemo(() => {
    const list = categorySearchText
      ? categories.filter(cat => cat.name?.toLowerCase().includes(categorySearchText.toLowerCase()))
      : categories;
    return sortTournamentCategories(list);
  }, [categories, categorySearchText]);

  // Категории, подходящие участнику по полу и возрасту (для модалки смены категории)
  const eligibleCategoriesForParticipant = useMemo(() => {
    if (!selectedParticipantForCategoryChange) return [];
    const participant = selectedParticipantForCategoryChange;
    const participantAge = participant.age ?? calculateAge(participant.dateOfBirth);
    const participantGender = (participant.gender || '').toLowerCase();
    const filtered = categories.filter((cat: any) => {
      const minAge = cat.minAge ?? cat.ageFrom ?? 0;
      const maxAge = cat.maxAge ?? cat.ageTo ?? 99;
      const ageOk = participantAge != null ? participantAge >= minAge && participantAge <= maxAge : true;
      const genderOk = !cat.gender || cat.gender === 'mixed' ||
        (participantGender && (cat.gender || '').toLowerCase() === participantGender);
      return ageOk && genderOk;
    });
    // Всегда включаем текущую категорию участника, чтобы она отображалась в селекте
    const currentCatId = participant.categoryId;
    const currentInList = filtered.some((c: any) => c.id === currentCatId || (c.categoryId ?? c.id) === currentCatId);
    if (currentCatId && !currentInList) {
      const currentCat = categories.find((c: any) => c.id === currentCatId || (c.categoryId ?? c.id) === currentCatId);
      if (currentCat) return sortTournamentCategories([currentCat, ...filtered]);
    }
    return sortTournamentCategories(filtered);
  }, [categories, selectedParticipantForCategoryChange]);

  // Группировка участников по категориям (с учетом уникальных участников)
  const participantsByCategory = useMemo(() => {
    const grouped: Record<number, any[]> = {};
    const participantIdsByCategory = new Map<number, Set<number>>();
    
    filteredParticipants.forEach(p => {
      if (p.categoryId) {
        // Получаем уникальный ID участника
        const participantId = p.userId || p.participantId || p.id;
        if (!participantId) return;
        
        // Инициализируем Set для категории, если его еще нет
        if (!participantIdsByCategory.has(p.categoryId)) {
          participantIdsByCategory.set(p.categoryId, new Set<number>());
          grouped[p.categoryId] = [];
        }
        
        // Добавляем участника только если его еще нет в этой категории
        const participantIds = participantIdsByCategory.get(p.categoryId)!;
        if (!participantIds.has(participantId)) {
          participantIds.add(participantId);
          grouped[p.categoryId].push(p);
        }
      }
    });
    return grouped;
  }, [filteredParticipants]);


  // Колонки для таблицы участников
  const participantColumns = [
    {
      title: 'Участник',
      key: 'participant',
      width: 250,
      render: (_: any, record: any) => {
        const initials = getInitials(record.firstName, record.lastName, record.middleName);
        return (
          <Space>
            <Avatar style={{ backgroundColor: '#1890ff' }}>
              {initials}
            </Avatar>
            <a 
              onClick={() => handleParticipantClick(record.userId || record.id)}
              style={{ cursor: 'pointer', color: '#1890ff' }}
            >
              {record.lastName} {record.firstName} {record.middleName || ''}
            </a>
          </Space>
        );
      },
    },
    {
      title: 'Категория',
      key: 'category',
      width: 300,
      render: (_: any, record: any) => {
        // У участника record.categoryId — это categories.id; в списке cat.id — tournament_categories.id, cat.categoryId — categories.id
        const category = categories.find(c => (c.categoryId ?? c.id) === record.categoryId);
        return category ? (
          <a 
            style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              if (onCategoryClick && category.id) {
                onCategoryClick(category.id);
              }
            }}
          >
            {category.name}
          </a>
        ) : '-';
      },
    },
    {
      title: 'Возраст',
      dataIndex: 'age',
      key: 'age',
      width: 100,
      render: (age: number | null | undefined) => age !== null && age !== undefined ? age : <span>-</span>,
    },
    {
      title: 'Класс',
      dataIndex: 'beltLevel',
      key: 'beltLevel',
      width: 120,
      render: (beltLevel: string | null | undefined) => beltLevel ? (
        <Tag color="purple">{beltLevel}</Tag>
      ) : <span>-</span>,
    },
    {
      title: 'Школа',
      dataIndex: 'clubName',
      key: 'school',
      render: (clubName: string | null | undefined) => clubName ? <span>{clubName}</span> : <span>-</span>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        // Извлекаем clubId участника из разных возможных мест
        // ПРИОРИТЕТ: record.clubId - это поле, которое мы сохранили при форматировании
        const participantClubId = record.clubId !== null && record.clubId !== undefined ? record.clubId :
                                  record.raw?.clubId !== null && record.raw?.clubId !== undefined ? record.raw.clubId :
                                  record.club?.id || 
                                  record.raw?.participant?.clubId || 
                                  record.raw?.participant?.club?.id ||
                                  record.raw?.club?.id ||
                                  record.raw?.user?.clubId ||
                                  record.raw?.user?.club?.id ||
                                  (record.raw?.participant && (record.raw.participant.clubId || record.raw.participant.club?.id)) ||
                                  (record.raw?.user && (record.raw.user.clubId || record.raw.user.club?.id));
        
        const canChange = canChangeCategory(participantClubId);
        const canUnreg = canUnregister(participantClubId);
        
        if (!canChange && !canUnreg) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        
        return (
          <Space size="small">
            {canChange && (
              <Button
                type="link"
                icon={<SwapOutlined />}
                onClick={() => handleChangeCategory(record)}
                size="small"
              >
                Изменить категорию
              </Button>
            )}
            {canUnreg && (
              <Popconfirm
                title="Снять участника с регистрации?"
                description="Вы уверены, что хотите снять этого участника с турнира?"
                onConfirm={() => handleUnregisterParticipant(record)}
                okText="Да"
                cancelText="Нет"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                >
                  Снять с участия
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // Колонки для таблицы участников в категориях
  const categoryParticipantColumns = [
    {
      title: 'Участник',
      key: 'participant',
      width: 250,
      render: (_: any, record: any) => {
        const initials = getInitials(record.firstName, record.lastName, record.middleName);
        return (
          <Space>
            <Avatar style={{ backgroundColor: '#1890ff' }}>
              {initials}
            </Avatar>
            <span>
              {record.lastName} {record.firstName} {record.middleName || ''}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Возраст',
      dataIndex: 'age',
      key: 'age',
      width: 100,
      render: (age: number | null | undefined) => age !== null && age !== undefined ? <span>{age}</span> : <span>-</span>,
    },
    {
      title: 'Класс',
      dataIndex: 'beltLevel',
      key: 'beltLevel',
      width: 120,
      render: (beltLevel: string | null | undefined) => beltLevel ? (
        <Tag color="purple">{beltLevel}</Tag>
      ) : <span>-</span>,
    },
    {
      title: 'Школа',
      dataIndex: 'clubName',
      key: 'school',
      render: (clubName: string | null | undefined) => clubName ? <span>{clubName}</span> : <span>-</span>,
    },
  ];

  const tabItems = [
    {
      key: 'participants',
      label: 'УЧАСТНИКИ',
      children: (
        <>
          <FiltersContainer>
            <Select
              placeholder="Категория"
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: 200 }}
              allowClear
            >
              {sortTournamentCategories(categories).map((cat: any) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Поиск категории"
              prefix={<SearchOutlined />}
              value={categorySearchText}
              onChange={(e) => setCategorySearchText(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
              allowClear
            />
          </FiltersContainer>

          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>Участник</Text>
            <Tag style={{ borderRadius: '50%', minWidth: '24px', textAlign: 'center' }}>
              {searchText || selectedClubId ? filteredParticipants.length : (total || participants.length)}
            </Tag>
            {total > 0 && !searchText && !selectedClubId && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                (загружено {participants.length} из {total})
              </Text>
            )}
          </div>

          <Table
            columns={participantColumns}
            dataSource={filteredParticipants}
            loading={loading}
            rowKey={(record, index) => {
              // Создаем уникальный ключ, комбинируя все возможные ID и индекс
              const id = record.id || record.participantId || record.userId;
              const categoryId = record.categoryId || '';
              // Используем комбинацию ID и categoryId для уникальности
              // Если ID все еще дублируется, добавляем индекс
              return id ? `${id}-${categoryId}-${index}` : `participant-${index}`;
            }}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
          
          {/* Индикатор загрузки и триггер для бесконечной прокрутки */}
          {hasMore && (
            <div ref={observerTarget} style={{ textAlign: 'center', padding: '20px', minHeight: '60px' }}>
              {loadingMore && <Spin size="large" />}
            </div>
          )}
          
          {!hasMore && !loadingMore && filteredParticipants.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              Загружено {filteredParticipants.length} из {total || filteredParticipants.length} участников
            </div>
          )}
          
          {!hasMore && !loadingMore && filteredParticipants.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              Участники не найдены
            </div>
          )}
        </>
      ),
    },
    {
      key: 'categories',
      label: 'КАТЕГОРИИ',
      children: (
        <>
          <FiltersContainer>
            <Input
              placeholder="Поиск категории"
              prefix={<SearchOutlined />}
              value={categorySearchText}
              onChange={(e) => setCategorySearchText(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
              allowClear
            />
          </FiltersContainer>

          <Collapse
            ghost
            items={filteredCategories.map(category => {
              // participantsByCategory ключ — categories.id; у category это categoryId (или id в старом формате)
              const categoryParticipants = participantsByCategory[category.categoryId ?? category.id] || [];
              return {
                key: category.id.toString(),
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <a 
                      style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onCategoryClick) {
                          onCategoryClick(category.id);
                        }
                      }}
                    >
                      {category.name}
                    </a>
                    <Tag style={{ borderRadius: '50%', minWidth: '24px', textAlign: 'center' }}>
                      {categoryParticipants.length}
                    </Tag>
                  </div>
                ),
                children: (
                  <Table
                    columns={categoryParticipantColumns}
                    dataSource={categoryParticipants}
                    loading={loading}
                    rowKey={(record, index) => {
                      // Создаем уникальный ключ, комбинируя все возможные ID, categoryId и индекс
                      const id = record.id || record.participantId || record.userId;
                      const categoryId = category.id || '';
                      // Используем комбинацию ID, categoryId и индекса для уникальности
                      return id ? `${id}-${categoryId}-${index}` : `participant-${categoryId}-${index}`;
                    }}
                    pagination={false}
                    size="small"
                  />
                ),
              };
            })}
          />
        </>
      ),
    },
  ];

  // Проверяем, опубликованы ли сетки
  const bracketsPublished = tournament?.bracketsPublished ?? false;
  
  // Проверяем, может ли пользователь видеть участников (менеджеры всегда видят)
  const isAdmin = user?.role === 'admin';
  const isOrganizer = tournament && user && tournament.organizerId === user.id;
  const isManagerOrCoach = user && (user.role === 'manager' || user.role === 'head_manager' || user.role === 'coach');
  const isTournamentClubMember = tournament && user && tournament.clubId && user.clubId && tournament.clubId === user.clubId;
  const canViewParticipants = isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember) || bracketsPublished;

  if (!canViewParticipants) {
    return (
      <Empty 
        description="Участники еще не опубликованы. Дождитесь публикации сеток менеджером турнира."
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24, width: '100%', overflow: 'visible' }}>
      {/* Левая боковая панель с клубами */}
      {clubs.length > 0 && (
        <SidebarContainer>
          {clubs.map((club) => (
            <ClubCard
              key={club.id}
              onClick={() => {
                if (selectedClubId === club.id) {
                  setSelectedClubId(undefined);
                } else {
                  setSelectedClubId(club.id);
                }
              }}
              selected={selectedClubId === club.id}
            >
              <Text ellipsis style={{ flex: 1 }}>
                {club.name}
              </Text>
              <Space size="small">
                <Tag style={{ borderRadius: '50%', minWidth: '24px', textAlign: 'center' }}>
                  {club.participantCount}
                </Tag>
                {canUnregisterClubAsOrganizer() && (
                  <Popconfirm
                    title="Снять команду с турнира?"
                    description={
                      <>
                        Будет удалено {club.participantCount} участников клуба «{club.name}».
                        {tournament?.bracketsPublished && (
                          <><br /><strong>Сетка будет пересформирована</strong> в затронутых категориях. Нумерация боёв изменится.</>
                        )}
                      </>
                    }
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleUnregisterClub(club);
                    }}
                    okText="Снять"
                    cancelText="Отмена"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={unregisteringClubId === club.id}
                      onClick={(e) => e.stopPropagation()}
                      title="Снять команду с турнира"
                    />
                  </Popconfirm>
                )}
              </Space>
            </ClubCard>
          ))}
        </SidebarContainer>
      )}

      {/* Основной контент */}
      <MainContentContainer>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        <Modal
          title="Статистика спортсмена"
          open={statisticsModalVisible}
          onCancel={() => {
            setStatisticsModalVisible(false);
            setSelectedParticipantId(null);
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          {selectedParticipantId && (
            <ParticipantStatistics participantId={selectedParticipantId} />
          )}
        </Modal>

        <Modal
          title="Изменение категории участника"
          open={changeCategoryModalVisible}
          onOk={handleCategoryChangeSubmit}
          onCancel={() => {
            setChangeCategoryModalVisible(false);
            setSelectedParticipantForCategoryChange(null);
            form.resetFields();
          }}
          okText="Сохранить"
          cancelText="Отмена"
          width={500}
          destroyOnClose
        >
          {selectedParticipantForCategoryChange && (
            <Form form={form} layout="vertical">
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong>Участник: </Typography.Text>
                <Typography.Text>
                  {selectedParticipantForCategoryChange.lastName} {selectedParticipantForCategoryChange.firstName} {selectedParticipantForCategoryChange.middleName || ''}
                </Typography.Text>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong>Текущая категория: </Typography.Text>
                <Tag color="blue">
                  {(() => {
                  const c = categories.find((c: any) => (c.categoryId ?? c.id) === selectedParticipantForCategoryChange.categoryId || c.id === selectedParticipantForCategoryChange.categoryId);
                  return c?.weightCategory?.name ?? c?.name ?? 'Не указана';
                })()}
                </Tag>
              </div>
              <Form.Item
                name="categoryId"
                label="Новая категория"
                rules={[{ required: true, message: 'Выберите категорию' }]}
              >
                <Select placeholder="Выберите категорию (только подходящие по полу и возрасту)">
                  {eligibleCategoriesForParticipant.map((category: any) => (
                    <Option key={category.id} value={category.id}>
                      {category.weightCategory?.name ?? category.name ?? `Категория ${category.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          )}
        </Modal>
      </MainContentContainer>
    </div>
  );
};

export default TournamentParticipants;
